import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { isNotNullOrUndefined } from '@weft/utils';
import type { Dayjs } from 'dayjs';
import React from 'react';
import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { useEffectOnce } from '../../../../hooks/use-effect-once';
import { getDefaultDates } from '../../../../pages/data-metering/utils/rangePickerUtils';
import { VyRangePickerMenu } from './VyRangePickerMenu';

type DataMeteringRangePickerProps = {
  size?: 'small' | 'medium';
};

export const VyRangePicker: React.FC<DataMeteringRangePickerProps> = ({ size = 'small' }) => {
  const [defaultFrom, defaultTo] = getDefaultDates();

  const [fromDay, setFromDay] = useFromDayParam(defaultFrom);
  const [toDay, setToDay] = useToDayParam(defaultTo);

  const onStartDateChange = (startDate: Dayjs | null) => {
    if (isNotNullOrUndefined(startDate)) {
      setFromDay(startDate.startOf('day'));

      if (isNotNullOrUndefined(toDay) && toDay.isBefore(startDate)) {
        setToDay(null);
      }
    } else {
      setFromDay(null);
    }
  };

  const onEndDateChange = (endDate: Dayjs | null) => {
    if (isNotNullOrUndefined(endDate)) {
      setToDay(endDate.endOf('day'));

      if (isNotNullOrUndefined(fromDay) && endDate.isBefore(fromDay)) {
        setFromDay(null);
      }
    } else {
      setToDay(null);
    }
  };

  useEffectOnce(() => {
    onStartDateChange(fromDay);
    onEndDateChange(toDay);
  });

  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-2">
        <VyRangePickerMenu
          fromDate={fromDay}
          onEndDateChange={onEndDateChange}
          onStartDateChange={onStartDateChange}
          toDate={toDay}
        >
          <TextField
            label="Date Range"
            value={fromDay && toDay ? `${fromDay.utc(true).format('MMM DD')} - ${toDay.format('MMM DD')}` : ''}
            size={size}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <ArrowDropDownIcon/>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: '240px',
            }}
          />
        </VyRangePickerMenu>
      </div>
    </div>
  );
};